import type { App } from 'vue'
import { createI18n } from 'vue-i18n'
import { ar, en, fr, hr, it } from 'vuetify/locale'
import { cookieRef } from '@layouts/stores/config'
import { themeConfig } from '@themeConfig'

const messages = Object.fromEntries(
  Object.entries(
    import.meta.glob<{ default: any }>('./locales/*.json', { eager: true }))
    .map(([key, value]) => [key.slice(10, -5), value.default]),
)

// Merge Vuetify locales
messages.en = { ...messages.en, $vuetify: { ...en, ...messages.en.$vuetify } }
messages.it = { ...messages.it, $vuetify: { ...it, ...messages.it.$vuetify } }
messages.ar = { ...messages.ar, $vuetify: { ...ar, ...messages.ar.$vuetify } }
messages.fr = { ...messages.fr, $vuetify: { ...fr, ...messages.fr.$vuetify } }
messages.hr = { ...messages.hr, $vuetify: { ...hr, ...messages.hr.$vuetify } }

const getByPath = (obj: any, path: string[]) => path.reduce((acc, key) => acc?.[key], obj)

const aliasMobileAppAsFlatKeys = () => {
  const enMobileApp = messages.en?.mobileApp
  if (!enMobileApp || typeof enMobileApp !== 'object')
    return

  const entries: Array<{ path: string[]; flatKey: string }> = []

  const walk = (node: any, path: string[]) => {
    if (!node || typeof node !== 'object')
      return

    Object.entries(node).forEach(([k, v]) => {
      const nextPath = [...path, k]
      if (typeof v === 'string') {
        if (v)
          entries.push({ path: nextPath, flatKey: v })

        return
      }

      walk(v, nextPath)
    })
  }

  walk(enMobileApp, [])

  Object.keys(messages).forEach(locale => {
    const localeMessages: any = (messages as any)[locale]
    const localeMobileApp = localeMessages?.mobileApp
    if (!localeMessages || !localeMobileApp)
      return

    entries.forEach(({ path, flatKey }) => {
      if (typeof localeMessages[flatKey] === 'string')
        return

      const localizedValue = getByPath(localeMobileApp, path)
      if (typeof localizedValue === 'string' && localizedValue)
        localeMessages[flatKey] = localizedValue
    })
  })
}

aliasMobileAppAsFlatKeys()

let _i18n: any = null

export const getI18n = () => {
  if (_i18n === null) {
    const langCookie = cookieRef<string | undefined>('language', undefined).value
    let locale = langCookie

    // Lingua di partenza del dominio whitelabel (vedi application.blade.php),
    // usata solo se l'utente non ha gia' una preferenza propria (cookie
    // "language" gia' impostato) - non scrive il cookie: resta un default
    // per questa visita/dominio, non una scelta esplicita dell'utente.
    if (!locale && typeof document !== 'undefined') {
      const whitelabelLocale = document.querySelector('meta[name="appforge-default-locale"]')?.getAttribute('content')
      if (whitelabelLocale && Object.keys(messages).includes(whitelabelLocale))
        locale = whitelabelLocale
    }

    if (!locale && typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0]
      const supportedLanguages = Object.keys(messages)
      if (supportedLanguages.includes(browserLang)) {
        locale = browserLang
        cookieRef<string | undefined>('language', undefined).value = locale
      }
    }

    if (!locale)
      locale = themeConfig.app.i18n.defaultLocale

    _i18n = createI18n({
      legacy: false,
      locale,
      fallbackLocale: 'en',
      messages,
    })
  }

  return _i18n
}

export default function (app: App) {
  app.use(getI18n())
}
